import {
    checkerProgressPills,
    convoyOwnerLocationLabel,
    deriveCheckerStatus,
    formatCheckinDateTime,
    formatCountdown,
} from "../utilities/checker"
import { useTickingClock } from "../hooks/useTickingClock"
import CheckerStatusPill from "./CheckerStatusPill"
import CheckerProgressPills from "./CheckerProgressPills"

const SAFE_COLOR = "#198754"
const DANGER_COLOR = "#dc3545"
const OVERDUE_COLOR = "#fd7e14"
const PENDING_COLOR = "#ffc107"
const SCHEDULED_COLOR = "#6c757d"

/**
 * A trip the signed-in user is watching (or invited to watch) as a
 * companion, modeled on the Android app's ConvoyTripCard: status pill,
 * owner avatar + trip title + owner/place, the same progress-pill row as
 * the owner's own Checker card, an inset next-check-in/missed/watching
 * stats panel, and either Accept/Decline (a still-pending invite) or
 * Ping/Escalate plus "Stop Watching" (an accepted one).
 *
 * @param {Object} trip - A row from GET /v1/checker/convoy
 * @param {boolean} isResponding - Disables Accept/Decline while one is in flight
 * @param {() => void} onAccept
 * @param {() => void} onDecline
 * @param {() => void} onPing
 * @param {() => void} onEscalate
 * @param {() => void} onStopWatching
 */
export default function ConvoyTripCard({
    trip,
    isResponding,
    onAccept,
    onDecline,
    onPing,
    onEscalate,
    onStopWatching,
}) {
    const now = useTickingClock()
    const { displayStatus, isOverdue, dueMillis } = deriveCheckerStatus(trip, now)

    // A still-pending companion invite shows the same card, with Accept/
    // Decline in place of Ping/Escalate -- those aren't meaningful to act on
    // before deciding whether to watch at all.
    const isPending = trip.companion_status === "invited"
    // An accepted companion sees a trip as soon as they accept, even before
    // the owner's start_time arrives -- shows as "Scheduled" rather than
    // misleadingly "Active" until then.
    const isScheduled = !isPending && displayStatus === "scheduled"
    const inDanger = trip.safety_status === "danger"

    const statusColor = isPending
        ? PENDING_COLOR
        : inDanger
          ? DANGER_COLOR
          : isOverdue
            ? OVERDUE_COLOR
            : isScheduled
              ? SCHEDULED_COLOR
              : SAFE_COLOR
    const statusLabel = isPending
        ? "Invite Pending"
        : inDanger
          ? "Needs help"
          : isOverdue
            ? "Overdue"
            : isScheduled
              ? "Scheduled"
              : "Active"

    const nextCheckinText = dueMillis == null ? "--:--" : formatCountdown(dueMillis - now)
    const needsAttention = inDanger || isOverdue
    const pills = checkerProgressPills(
        trip.expected_checkins_count,
        trip.completed_checkins_count,
        needsAttention,
    )

    const ownerName =
        [trip.owner_first_name, trip.owner_last_name].filter(Boolean).join(" ") ||
        trip.owner_username ||
        "Unknown"

    return (
        <article
            className={`checker-trip-card${
                isPending || inDanger || isOverdue ? " checker-trip-card-danger" : ""
            }`}
        >
            <div className="checker-trip-status-row checker-trip-status-row-end">
                <CheckerStatusPill
                    label={statusLabel}
                    color={statusColor}
                    filled
                    pulsing={!isPending && !inDanger && !isOverdue && !isScheduled}
                />
            </div>

            <div className="checker-convoy-heading">
                <span className="checker-companion-avatar checker-convoy-owner-avatar">
                    {trip.owner_profile_picture_url ? (
                        <img src={trip.owner_profile_picture_url} alt="" />
                    ) : (
                        <span>{ownerName.slice(0, 2).toUpperCase()}</span>
                    )}
                </span>
                <div className="checker-trip-heading">
                    <span className="checker-trip-title">{trip.title}</span>
                    {/* Where the owner is now (as on Android), then the
                        trip's own destination. */}
                    <div className="checker-trip-meta">
                        {ownerName} · {convoyOwnerLocationLabel(trip)}
                    </div>
                    <div className="checker-trip-meta">
                        {trip.place_label || "Destination not set"}
                    </div>
                </div>
            </div>

            <CheckerProgressPills pills={pills} />

            <div className="checker-convoy-stats">
                <div className="checker-convoy-stat">
                    <div
                        className={`checker-trip-stat-value${isOverdue ? " checker-trip-stat-danger" : ""}`}
                    >
                        {nextCheckinText}
                    </div>
                    <div className="checker-trip-stat-label">Next check-in</div>
                </div>
                <div className="checker-convoy-stat-divider"></div>
                <div className="checker-convoy-stat">
                    <div
                        className={`checker-trip-stat-value${
                            trip.missed_checkins_count > 0 ? " checker-trip-stat-danger" : ""
                        }`}
                    >
                        {trip.missed_checkins_count || 0}
                    </div>
                    <div className="checker-trip-stat-label">Missed</div>
                </div>
                <div className="checker-convoy-stat-divider"></div>
                <div className="checker-convoy-stat">
                    <div className="checker-trip-stat-value">
                        {trip.convoy_count || 0}
                    </div>
                    <div className="checker-trip-stat-label">Watching</div>
                </div>
            </div>

            <div className="checker-trip-meta">
                Last check-in: {formatCheckinDateTime(trip.last_checkin_at)}
            </div>

            {isPending ? (
                <div className="checker-trip-checkin-row">
                    <button
                        type="button"
                        className="btn checker-btn-safe"
                        disabled={isResponding}
                        onClick={onAccept}
                    >
                        Accept
                    </button>
                    <button
                        type="button"
                        className="btn checker-btn-neutral"
                        disabled={isResponding}
                        onClick={onDecline}
                    >
                        Decline
                    </button>
                </div>
            ) : (
                <>
                    <div className="checker-trip-checkin-row">
                        <button
                            type="button"
                            className="btn checker-btn-primary"
                            disabled={!needsAttention}
                            onClick={onPing}
                        >
                            <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
                            Ping
                        </button>
                        <button
                            type="button"
                            className="btn checker-btn-neutral"
                            onClick={onEscalate}
                        >
                            <i
                                className="bi bi-exclamation-triangle me-1"
                                aria-hidden="true"
                            ></i>
                            Escalate
                        </button>
                    </div>
                    <button
                        type="button"
                        className="checker-convoy-stop-watching"
                        onClick={onStopWatching}
                    >
                        Stop Watching
                    </button>
                </>
            )}
        </article>
    )
}
